import { Button, DialogActions, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'

// others
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  CreateReferenceInput,
  ReferralSource,
  useCreateReferenceMutation,
  useFetchAllReferralSourcesLazyQuery,
  useUpdateReferenceMutation
} from '../../../generated'
import { AddReferenceProps } from '../../interfaceTypes/props'

// components
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { GRAPHQL_QUERY_POLICY, PAGE_LIMIT } from '../../constants'
import { referenceSchema } from '../../validationSchema'
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from '../common/CustomModal'
import CheckboxController from '../common/CheckboxController'

const AddReference = ({ isOpen, handleClose, refetch, reference }: AddReferenceProps) => {
  const [referralSources, setReferralSources] = useState<ReferralSource[]>([])
  const methods = useForm<CreateReferenceInput>({
    resolver: yupResolver(referenceSchema),
    defaultValues: {
      sourceTypeId: reference?.referralSource?.id ?? '',
      reference: reference?.referral ?? '',
      eligibleFor1099Report: reference?.eligibleFor1099Report ?? false,
      active: reference?.active ?? true
    }
  })

  const { control } = methods
  const [createReference, { loading }] = useCreateReferenceMutation({
    onError: error => {
      console.log('Error creating reference:', error)
    },
    onCompleted: data => {
      const { createReference } = data
      if (createReference) {
        Alert.success('Referral Sources Created!')
        handleClose()
        refetch?.()
        reset()
      }
    }
  })

  const [updateReference, { loading: updating }] = useUpdateReferenceMutation({
    onError: error => {
      console.log('Error creating referral source:', error)
    },
    onCompleted: data => {
      const { updateReference } = data
      if (updateReference) {
        Alert.success('Referral Sources Updated!')
        handleClose()
        refetch?.()
        reset()
      }
    }
  })

  const [fetchAllReferralSources] = useFetchAllReferralSourcesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      const { referralSources } = data.fetchAllReferralSources
      setReferralSources(referralSources as ReferralSource[])
    },
    onError(error) {
      console.error('Error fetching referral sources', error)
      setReferralSources([])
    }
  })

  const { handleSubmit, reset } = methods
  const onSubmit = async (data: CreateReferenceInput) => {
    const { reference: referral, ...rest } = data
    reference?.id
      ? await updateReference({
          variables: {
            updateReferenceInput: {
              id: reference.id,
              reference: referral,
              ...rest
            }
          }
        })
      : await createReference({
          variables: {
            createReferenceInput: {
              reference: referral,
              ...rest
            }
          }
        })
  }

  const onClose = () => {
    handleClose()
    reset()
  }

  useEffect(() => {
    fetchAllReferralSources({
      variables: {
        fetchAllSources: {
          paginationOptions: {
            limit: PAGE_LIMIT,
            page: 1
          }
        }
      }
    })
  }, [fetchAllReferralSources])

  return (
    <CustomModal title={`${reference?.id ? 'Edit' : 'Add'}  Referral Sources`} isOpen={isOpen} handleClose={onClose}>
      <FormProvider {...methods}>
        <form
          onSubmit={event => {
            event.stopPropagation()
            return handleSubmit(onSubmit)(event)
          }}
        >
          <FormControl variant="outlined" sx={{ marginBottom: 1 }} fullWidth>
            <InputLabel>Referral Type *</InputLabel>
            <Controller
              control={control}
              name={`sourceTypeId`}
              render={({ field }) => (
                <Select {...field} label={`Referral Type`} required>
                  {referralSources.length > 0 ? (
                    referralSources.map(({ sourceType, id }) => (
                      <MenuItem value={id} key={id}>
                        <Typography variant="body1">{sourceType}</Typography>
                      </MenuItem>
                    ))
                  ) : (
                    <Typography variant="body1" color="textSecondary" pl={2}>
                      No types available
                    </Typography>
                  )}
                </Select>
              )}
            />
          </FormControl>

          <CustomController controllerLabel="Reference" fullWidth controllerName="reference" required />

          <CheckboxController label={'Active'} name="active" />
          <CheckboxController label={'Eligible for 1099'} name="eligibleFor1099Report" />

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" type="submit" disabled={loading || updating}>
              {reference?.id ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default AddReference
